import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi, type AuthTokens, type AuthUser } from './authApi';

const REFRESH_TOKEN_KEY = 'gymcoach_refresh_token';
const USER_KEY = 'gymcoach_user';
// The access token is persisted (with its expiry) so a reload or a second tab
// can reuse a still-valid token instead of rotating the refresh token again —
// needless rotations are what trip the backend's reuse detection.
const ACCESS_TOKEN_KEY = 'gymcoach_access_token';
const ACCESS_EXP_KEY = 'gymcoach_access_exp';
// Cross-tab lock name so only one tab refreshes (rotates) at a time.
const REFRESH_LOCK = 'gymcoach-token-refresh';
// Treat a token as usable only if it has >30s of life left.
const EXP_SKEW_SECS = 30;

const nowSecs = () => Math.floor(Date.now() / 1000);

function readStoredAccess(): { token: string; exp: number } | null {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const exp = Number(localStorage.getItem(ACCESS_EXP_KEY) || 0);
  return token && exp ? { token, exp } : null;
}

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Silently refreshes the access token. Returns false if refresh fails. */
  refresh: () => Promise<boolean>;
  /** Always returns the latest access token, even immediately after a refresh. */
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Avoid concurrent refresh calls within this tab.
  const refreshingRef = useRef<Promise<boolean> | null>(null);
  // accessToken/logout callbacks need the latest token without retriggering effects
  const accessTokenRef = useRef<string | null>(null);

  const applyAccess = useCallback((token: string, exp: number) => {
    accessTokenRef.current = token;
    setAccessToken(token);
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(ACCESS_EXP_KEY, String(exp));
  }, []);

  const storeSession = useCallback((tokens: AuthTokens, authUser: AuthUser) => {
    applyAccess(tokens.accessToken, nowSecs() + tokens.expiresIn);
    setUser(authUser);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
  }, [applyAccess]);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ACCESS_EXP_KEY);
  }, []);

  const refresh = useCallback((): Promise<boolean> => {
    if (refreshingRef.current) return refreshingRef.current;

    // The actual rotation. Runs inside a cross-tab lock so two tabs can't
    // present the same refresh token concurrently (which the backend treats as
    // reuse and responds to by revoking the whole token family).
    const rotate = async (): Promise<boolean> => {
      // Another tab may have already refreshed while we waited for the lock —
      // if a fresh token is now in storage, adopt it instead of rotating again.
      const stored = readStoredAccess();
      if (stored && stored.exp > nowSecs() + EXP_SKEW_SECS && stored.token !== accessTokenRef.current) {
        applyAccess(stored.token, stored.exp);
        return true;
      }
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefresh) return false;
      try {
        const tokens = await authApi.refresh(storedRefresh);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        applyAccess(tokens.accessToken, nowSecs() + tokens.expiresIn);
        return true;
      } catch {
        clearSession();
        return false;
      }
    };

    const run = navigator.locks?.request
      ? navigator.locks.request(REFRESH_LOCK, rotate)
      : rotate();
    const promise = Promise.resolve(run).finally(() => { refreshingRef.current = null; });
    refreshingRef.current = promise;
    return promise;
  }, [applyAccess, clearSession]);

  // On mount: restore the session. If we still hold a valid (unexpired) access
  // token, use it directly — do NOT rotate the refresh token on every load.
  useEffect(() => {
    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser) as AuthUser);
      } catch {
        localStorage.removeItem(USER_KEY);
      }
    }
    const stored = readStoredAccess();
    if (stored && stored.exp > nowSecs() + EXP_SKEW_SECS) {
      accessTokenRef.current = stored.token;
      setAccessToken(stored.token);
      setLoading(false);
    } else if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
      refresh().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    storeSession(res, { userId: res.userId, email: res.email, displayName: res.displayName });
  }, [storeSession]);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    const res = await authApi.register(email, password, displayName);
    storeSession(res, { userId: res.userId, email: res.email, displayName: res.displayName });
  }, [storeSession]);

  const logout = useCallback(async () => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefresh && accessTokenRef.current) {
      try { await authApi.logout(accessTokenRef.current, storedRefresh); } catch { /* ignore */ }
    }
    clearSession();
  }, [clearSession]);

  const getToken = useCallback(() => accessTokenRef.current, []);

  const value = useMemo(
    () => ({ user, accessToken, loading, login, register, logout, refresh, getToken }),
    [user, accessToken, loading, login, register, logout, refresh, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * fetch wrapper that automatically adds the Authorization header and retries
 * once after a silent token refresh on 401.
 */
export async function authFetch(
  url: string,
  options: RequestInit,
  getToken: () => string | null,
  doRefresh: () => Promise<boolean>,
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...options, headers });
  // 401/403 = normal expired-token rejection. 500 is included defensively: a
  // misbehaving authorizer can surface an expired token as a 500 rather than a
  // 403, and we never want a stale token to wedge every action until reload.
  const status = res.status;
  if (status !== 401 && status !== 403 && status !== 500) return res;
  // Always try to refresh on 401/403 (doRefresh no-ops without a stored refresh
  // token); only require an in-memory token before refreshing on a 500, so we
  // don't mask a genuine server error.
  if (status === 500 && !token) return res;

  const refreshed = await doRefresh();
  if (!refreshed) return res;

  const newToken = getToken();
  if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
  return fetch(url, { ...options, headers });
}
